import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { UserAccessApi } from '../../../common-files/user-access-api';

// grant single view-permissions: Establishment -> Role (the user's assignment there)
// -> View -> Permission, with a search inside the chosen view. The pool is every
// permission carried by a role below the admin's own, so a permission of another
// role can be given on its own. Stays open for several grants; closes with `true`
// when anything was granted. data = { sumId, name, establishments[{establishmentId, name, roles[]}], api }.
// (the central place comes in with establishmentId 0.)
@Component({
  selector: 'app-add-permission-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './add-permission-dialog.component.html',
  styleUrls: ['./add-permission-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class AddPermissionDialogComponent implements OnInit {
  establishmentId = new FormControl<number | null>(null);
  suremId = new FormControl<number | null>(null);
  viewName = new FormControl<string>('');
  search = new FormControl('');

  permissions: any[] = []; // assignable catalogue (svpId, viewName, viewLabel, permission, permLabel, roles)
  granted = new Set<number>(); // svpIds active on the selected role
  isLoading: boolean = false;
  private changed: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AddPermissionDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { sumId: number; name: string; establishments: any[]; api: UserAccessApi },
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    const activeEst = Number(localStorage.getItem('activeEstablishmentId'));
    const first = this.data.establishments.find((e) => e.establishmentId === activeEst) || this.data.establishments[0];
    if (first) this.establishmentId.setValue(first.establishmentId);
    this.onEstablishmentChange();

    this.isLoading = true;
    this.data.api.listAssignablePermissions().subscribe({
      next: (permissions) => {
        this.permissions = permissions;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load permissions.');
      },
    });
  }

  selectedEstablishment(): any {
    return this.data.establishments.find((e) => e.establishmentId === this.establishmentId.value) || null;
  }

  rolesOfEstablishment(): any[] {
    return this.selectedEstablishment()?.roles || [];
  }

  selectedRole(): any {
    return this.rolesOfEstablishment().find((r: any) => r.suremId === this.suremId.value) || null;
  }

  onEstablishmentChange(): void {
    const roles = this.rolesOfEstablishment();
    this.suremId.setValue(roles.length === 1 ? roles[0].suremId : null);
    this.onRoleChange();
  }

  onRoleChange(): void {
    this.granted.clear();
    const role = this.selectedRole();
    for (const v of role?.views || []) {
      for (const p of v.permissions || []) if (p.isActive) this.granted.add(Number(p.svpId));
    }
    this.viewName.setValue('');
    this.search.setValue('');
  }

  // distinct views of the catalogue, for the View dropdown
  viewOptions(): { viewName: string; viewLabel: string }[] {
    const views: { viewName: string; viewLabel: string }[] = [];
    for (const p of this.permissions) {
      if (!views.some((v) => v.viewName === p.viewName)) views.push({ viewName: p.viewName, viewLabel: p.viewLabel });
    }
    return views;
  }

  canPick(): boolean {
    return !!this.selectedRole() && !!this.viewName.value;
  }

  // permissions of the chosen view, narrowed by the search
  visiblePermissions(): any[] {
    if (!this.canPick()) return [];
    const term = (this.search.value || '').trim().toLowerCase();
    return this.permissions.filter(
      (p) =>
        p.viewName === this.viewName.value &&
        (!term || `${p.permLabel} ${p.permission} ${p.roles}`.toLowerCase().includes(term))
    );
  }

  isGranted(svpId: number): boolean {
    return this.granted.has(Number(svpId));
  }

  grant(permission: any): void {
    const role = this.selectedRole();
    if (!role || this.isGranted(permission.svpId)) return;
    this.isLoading = true;
    this.data.api.grantPermission(this.data.sumId, role.suremId, Number(permission.svpId)).subscribe({
      next: (res: any) => {
        this.granted.add(Number(permission.svpId));
        this.changed = true;
        this.isLoading = false;
        this.toastService.success(res?.message || 'Permission granted.');
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to grant the permission.');
      },
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
